/**
 * The monthly pack. Sections are kept in Admin: each has its owners, the
 * named fields they write, and the "From the system" rows shown beside them
 * (live, read-only; the owner writes the why). A pack covers a month and has
 * its sections due date and meeting date. Owners save and submit their
 * section; a director approves it or sends it back. Issuing freezes the whole
 * pack (what was written, every panel's figures, the measures at a glance,
 * obligations inside their lead time) as a numbered version that never
 * changes; it's what observers and the PDF see.
 */
import { addDays, dayLabel, type ISODate } from "./dates";
import { money } from "./money";
import { engineInput } from "./model";
import { forecast, salesNeeded, type Forecast } from "./engine";
import { GATE_LABEL, type FinanceModel, type Role } from "./types";
import { actionRef, actionState, dueOf } from "./actions";
import { decisionRef } from "./decisions";
import { OBLIGATION_STATE_LABEL, obligationNow, obligationRef } from "./obligations";
import { releaseStats } from "./releases";
import { bandOf, riskRef, score } from "./risks";
import { MEASURE_STATUS_LABEL, formatMeasure, isLive, measureAt, nextMonth, pullMeasure, type MeasureStatus } from "./measures";
import type { RegistersModel } from "./registers";
import { APPENDIX_KEYS, packAppendices, paceOf, type Appendix, type AppendixKey } from "./appendices";

export type SectionState = "todo" | "draft" | "submitted" | "approved";
export const SECTION_STATE_LABEL: Record<SectionState, string> = { todo: "To do", draft: "Drafting", submitted: "Submitted", approved: "Approved" };

export interface PackField {
  id: string;
  label: string;
  /** Shown under the field as a prompt. */
  hint: string;
  /** A board pack field compiled from a monthly pack's field, month by month; not written. */
  from?: { sectionId: string; fieldId: string } | null;
}

export type PackKind = "monthly" | "board";
export const PACK_TITLE: Record<PackKind, string> = { monthly: "Monthly pack", board: "Board pack" };
/** What a board section asks of the board. */
export type BoardAsk = "note" | "approve" | "discuss" | "decide";
export const BOARD_ASK_LABEL: Record<BoardAsk, string> = { note: "To note", approve: "To approve", discuss: "To discuss", decide: "To decide" };

/** A row the system can put in a section's "From the system" panel. */
export type PanelKey =
  | "cash"
  | "gate"
  | "first_overdrawn"
  | "sales_needed"
  | "what_ifs"
  | "related_party"
  | "customer_cash"
  | "paying"
  | "objectives"
  | "measures"
  | "changes_open"
  | "longest_to_live"
  | "live_unsigned"
  | "high_risks"
  | "obligations"
  | "actions"
  | "decisions_waiting"
  | "decisions_taken"
  | "actions_list"
  | "tier1_risks";

export const PANELS: Record<PanelKey, { label: string; group: string }> = {
  cash: { label: "Cash in bank", group: "Finance" },
  gate: { label: "Spend gate and 13-week low", group: "Finance" },
  first_overdrawn: { label: "First week overdrawn", group: "Finance" },
  sales_needed: { label: "New sales needed next month", group: "Finance" },
  what_ifs: { label: "Levers applied, not yet approved", group: "Finance" },
  related_party: { label: "Related-party lines", group: "Finance" },
  customer_cash: { label: "Customer cash in the month", group: "Xero" },
  paying: { label: "Paying customers, sites and MRR", group: "Sales Hub" },
  objectives: { label: "Objectives and their status", group: "Sales Hub" },
  measures: { label: "This section's measures", group: "Measures" },
  changes_open: { label: "Statutory changes still open", group: "Release log" },
  longest_to_live: { label: "Longest days to go live", group: "Release log" },
  live_unsigned: { label: "Live without sign-off", group: "Release log" },
  high_risks: { label: "Risks in the top two bands", group: "Registers" },
  obligations: { label: "Obligations overdue and due soon", group: "Registers" },
  actions: { label: "Actions open and overdue", group: "Registers" },
  decisions_waiting: { label: "Decisions waiting", group: "Registers" },
  decisions_taken: { label: "Decisions taken in the period", group: "Registers" },
  actions_list: { label: "Each action carried forward", group: "Registers" },
  tier1_risks: { label: "Each first-tier risk", group: "Registers" },
};
export const PANEL_KEYS = Object.keys(PANELS) as PanelKey[];

export interface PackSection {
  id: string;
  kind: PackKind;
  title: string;
  /** Board sections: what the board is asked to do with it. */
  ask: BoardAsk | null;
  ownerIds: string[];
  fields: PackField[];
  panels: PanelKey[];
  active: boolean;
  sortOrder: number;
}

export interface PackSettings {
  /** The meeting falls on this weekday (1 Monday … 7 Sunday)… */
  meetingWeekday: number;
  /** …the nth of the month after the one covered (5: the last). */
  meetingNth: number;
  /** Sections are due this many days before the meeting. */
  dueDaysBefore: number;
  /** A pack can be issued with sections not approved, the gap stated on its front page. */
  issueWithGaps: boolean;
  /** A section's own owners can approve it, if they're directors. */
  ownersApprove: boolean;
  /** The line at the foot of every page. */
  footer: string;
  /** The chart appendices, in order. */
  appendices: AppendixKey[];
  /** How many months the charts go back, the month covered included. */
  chartMonths: number;
  /** The monthly meeting's time, London. */
  meetingStart: string;
  meetingEnd: string;
  /** Who the monthly meeting invites, by role. */
  guestRoles: Role[];
  /** How many months of monthly meetings are planned ahead. */
  planAhead: number;
  /** The board meets on this weekday… */
  boardWeekday: number;
  /** …the nth of the month… */
  boardNth: number;
  /** …this many months after the quarter ends. */
  boardMonthsAfter: number;
  /** Board papers are due this many days before. */
  boardDueDaysBefore: number;
  /** Chairs the board (named on the board pack's front page). */
  chairId: string | null;
  /** Signs off every meeting's minutes, unless they chaired it. */
  minutesById: string | null;
  /** Signs off when the one named chaired the meeting. */
  minutesDeputyId: string | null;
  /** Drafts the minutes: reminded and chased. */
  minutesDrafterId: string | null;
  /** Chairs the monthly meeting. */
  monthlyChairId: string | null;
  /** Minutes are circulated within this many working days. */
  minutesDays: number;
}
export const DEFAULT_PACK_SETTINGS: PackSettings = { meetingWeekday: 3, meetingNth: 3, dueDaysBefore: 5, issueWithGaps: false, ownersApprove: false, footer: "Private and confidential.", appendices: [...APPENDIX_KEYS], chartMonths: 12, meetingStart: "14:00", meetingEnd: "15:30", guestRoles: ["director", "team"], planAhead: 12, boardWeekday: 3, boardNth: 1, boardMonthsAfter: 2, boardDueDaysBefore: 12, chairId: null, minutesById: null, minutesDeputyId: null, minutesDrafterId: null, monthlyChairId: null, minutesDays: 5 };
export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export interface PackEntry {
  sectionId: string;
  /** Field id → what the owner wrote. */
  fields: Record<string, string>;
  state: SectionState;
  savedBy: string | null;
  savedAt: string | null;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  /** Why it was sent back, until it's submitted again. */
  returnedNote: string | null;
  returnedBy: string | null;
  returnedAt: string | null;
}

export interface Pack {
  id: string;
  kind: PackKind;
  /** The month covered, "YYYY-MM"; a board pack's is the quarter's last. */
  month: string;
  /** The months it covers: one, or a board pack's three. */
  months: string[];
  meetingOn: ISODate;
  dueOn: ISODate;
  status: "open" | "issued";
  /** Times issued. */
  version: number;
  issuedAt: string | null;
  entries: PackEntry[];
  createdAt: string;
}

const MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** "September 2026". */
export const packCovers = (month: string) => `${MONTH_FULL[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
/** "September 2026", or a board pack's "October to December 2026". */
export const packPeriod = (p: Pick<Pack, "kind" | "month" | "months">) =>
  p.kind === "board" && p.months.length > 1 ? `${MONTH_FULL[Number(p.months[0]!.slice(5, 7)) - 1]}${p.months[0]!.slice(0, 4) === p.month.slice(0, 4) ? "" : ` ${p.months[0]!.slice(0, 4)}`} to ${packCovers(p.month)}` : packCovers(p.month);
/** The quarter ending with a month: its three months. */
export const quarterTo = (month: string) => [prevOfMonth(prevOfMonth(month)), prevOfMonth(month), month];
const prevOfMonth = (m: string) => addDays(`${m}-01`, -1).slice(0, 7);
/** A quarter's last month, by the financial year. */
export const isQuarterEnd = (month: string, yearStartMonth: number) => (Number(month.slice(5, 7)) - yearStartMonth + 12) % 3 === 2;
/** The last quarter to end before this month. */
export function lastQuarterEnd(today: ISODate, yearStartMonth: number): string {
  let m = prevOfMonth(today.slice(0, 7));
  while (!isQuarterEnd(m, yearStartMonth)) m = prevOfMonth(m);
  return m;
}
/** A section with only compiled fields: nothing to write, ready as it stands. */
export const isCompiled = (s: Pick<PackSection, "fields">) => s.fields.length > 0 && s.fields.every((f) => !!f.from);

export const emptyEntry = (sectionId: string): PackEntry => ({ sectionId, fields: {}, state: "todo", savedBy: null, savedAt: null, submittedBy: null, submittedAt: null, approvedBy: null, approvedAt: null, returnedNote: null, returnedBy: null, returnedAt: null });
export const entryFor = (pack: Pick<Pack, "entries">, sectionId: string) => pack.entries.find((e) => e.sectionId === sectionId) ?? emptyEntry(sectionId);

const isWorkingDay = (d: ISODate, holidays: ReadonlySet<string>) => {
  const w = new Date(`${d}T00:00:00Z`).getUTCDay();
  return w !== 0 && w !== 6 && !holidays.has(d);
};

/**
 * The meeting (the nth weekday of the month after) and the sections due date,
 * from the settings. A meeting that lands on a bank holiday moves to the next
 * working day; a due date on a weekend or bank holiday moves to the working
 * day before.
 */
export function packDates(month: string, s: Pick<PackSettings, "meetingWeekday" | "meetingNth" | "dueDaysBefore">, holidays: ReadonlySet<string> = new Set()): { meetingOn: ISODate; dueOn: ISODate } {
  const first = `${nextMonth(month)}-01`;
  const want = s.meetingWeekday % 7; // getUTCDay: Sunday 0
  const firstDay = new Date(`${first}T00:00:00Z`).getUTCDay();
  let meetingOn = addDays(first, (want - firstDay + 7) % 7 + 7 * (Math.min(s.meetingNth, 5) - 1));
  // the fifth may not exist: the last one in the month
  while (meetingOn.slice(0, 7) !== first.slice(0, 7)) meetingOn = addDays(meetingOn, -7);
  while (holidays.has(meetingOn)) meetingOn = addDays(meetingOn, 1);
  return { meetingOn, dueOn: dueBefore(meetingOn, s.dueDaysBefore, holidays) };
}

/** A board pack's meeting (the nth weekday, months after the quarter ends) and papers due date. */
export function boardDates(month: string, s: Pick<PackSettings, "boardWeekday" | "boardNth" | "boardMonthsAfter" | "boardDueDaysBefore">, holidays: ReadonlySet<string> = new Set()): { meetingOn: ISODate; dueOn: ISODate } {
  let m = month;
  for (let i = 1; i < Math.max(1, s.boardMonthsAfter); i++) m = nextMonth(m);
  return packDates(m, { meetingWeekday: s.boardWeekday, meetingNth: s.boardNth, dueDaysBefore: s.boardDueDaysBefore }, holidays);
}

/** `days` before the meeting, back to a working day. */
export function dueBefore(meetingOn: ISODate, days: number, holidays: ReadonlySet<string> = new Set()): ISODate {
  let due = addDays(meetingOn, -days);
  while (!isWorkingDay(due, holidays)) due = addDays(due, -1);
  return due;
}

/** A meeting's pack: the monthly pack for the month it covers, or the board pack it meets on. */
export const packOfMeeting = <P extends Pick<Pack, "kind" | "month" | "meetingOn">>(m: { kind: string; month: string | null; date: string }, packs: P[]): P | undefined =>
  m.kind === "monthly" ? packs.find((p) => p.kind === "monthly" && p.month === m.month) : m.kind === "board" ? packs.find((p) => p.kind === "board" && p.meetingOn === m.date) : undefined;

/** The pack's own sections (monthly or board), in use. */
export const sectionsOf = (pack: Pick<Pack, "kind">, sections: PackSection[]) => sections.filter((s) => s.active && s.kind === pack.kind);

/** Sections approved (or compiled) out of those in the pack. */
export function packProgress(pack: Pick<Pack, "entries" | "kind">, sections: PackSection[]) {
  const live = sectionsOf(pack, sections);
  return { approved: live.filter((s) => isCompiled(s) || entryFor(pack, s.id).state === "approved").length, total: live.length };
}

export const canWriteSection = (me: { id: string }, s: Pick<PackSection, "ownerIds">) => s.ownerIds.includes(me.id);
export const canApproveSection = (me: { id: string; role: Role }, s: Pick<PackSection, "ownerIds">, settings: Pick<PackSettings, "ownersApprove">) =>
  me.role === "director" && (settings.ownersApprove || !s.ownerIds.includes(me.id));

/* ------------------------------------------------------------------ */
/* From the system                                                     */
/* ------------------------------------------------------------------ */

export interface PanelRow {
  key: PanelKey;
  label: string;
  value: string;
  source: string;
  /** The screen it comes from. */
  href: string | null;
}

export interface PackContext {
  model: FinanceModel;
  reg: RegistersModel;
  /** The month covered. */
  month: string;
  /** The months covered, when more than one (a board pack's quarter). */
  months?: string[];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const first = (name: string | null | undefined) => (name ? name.split(/\s+/)[0]! : "");

const krValue = (v: number, unit: string) => (unit === "gbp" ? `£${Math.round(v).toLocaleString("en-GB")}` : (Math.round(v * 10) / 10).toLocaleString("en-GB"));

/** The rows for a section's panel, live from what the system holds. A row it can't work out is left out. */
export function panelRows(keys: PanelKey[], ctx: PackContext & { sectionTitle: string }): PanelRow[] {
  const { model, reg, month } = ctx;
  let fc: Forecast | null | undefined;
  const fcast = () => {
    if (fc === undefined) {
      const input = engineInput(model, "approved");
      fc = input ? forecast(input) : null;
    }
    return fc;
  };
  const person = (id: string | null) => reg.people.find((p) => p.id === id)?.name ?? null;
  const out: PanelRow[] = [];
  const row = (key: PanelKey, label: string, value: string, source: string, href: string | null) => out.push({ key, label, value, source, href });

  for (const key of keys) {
    switch (key) {
      case "cash": {
        const b = model.balance;
        if (b) row(key, "Cash in bank", `${money(b.amount)} on ${dayLabel(b.asAt)}`, b.source === "xero" ? "Xero" : "Bank balance", "/finance");
        break;
      }
      case "gate": {
        const f = fcast();
        if (f) row(key, "Spend gate", `${GATE_LABEL[f.gate]} · 13-week low ${money(f.low.close)}, week of ${dayLabel(f.low.start)}`, "Finance, approved forecast", "/finance/cash");
        break;
      }
      case "first_overdrawn": {
        const f = fcast();
        if (f) row(key, "First week overdrawn", f.firstOverdrawn ? dayLabel(f.firstOverdrawn) : `None in the ${model.settings.runOutWeeks} weeks forecast`, "Finance, contracted income", "/finance/cash");
        break;
      }
      case "sales_needed": {
        const f = fcast();
        if (!f) break;
        const s = salesNeeded(f, () => model.thresholds.red);
        const m = s.months[1];
        if (m) row(key, `New sales needed in ${packCovers(m)}`, s.needed[1]! > 0 ? `${money(s.needed[1]!)} to stay above Stop` : "None to stay above Stop", "Finance", "/finance/cash");
        break;
      }
      case "what_ifs": {
        const ls = model.levers.filter((l) => l.applied && l.status !== "approved");
        row(key, "Levers applied, not yet approved", ls.length ? ls.map((l) => l.title).join(" · ") : "None", "Finance", "/finance/levers");
        break;
      }
      case "related_party": {
        const ls = model.commitments.filter((c) => c.active && c.connectedDirectorId);
        row(key, "Related-party lines", ls.length ? ls.map((c) => `${c.name} (${first(person(c.connectedDirectorId))})`).join(" · ") : "None", "Finance", "/finance/planner");
        break;
      }
      case "customer_cash": {
        if (model.synced.connections.xero.mode === "off") break;
        const rs = model.synced.receipts.filter((r) => r.date.slice(0, 7) === month);
        row(key, `Customer cash, ${packCovers(month)}`, rs.length ? `${money(rs.reduce((a, r) => a + r.amount, 0))} from ${plural(rs.length, "receipt")}` : "None", "Xero", "/finance/billing");
        break;
      }
      case "paying": {
        const c = pullMeasure("customers", month, model, reg);
        const s = pullMeasure("sites_billed", month, model, reg);
        const r = pullMeasure("mrr", month, model, reg);
        if (c && s && r) row(key, "Paying customers", `${plural(c.value, "customer")} · ${plural(s.value, "site")} · ${money(r.value)} a month`, "Sales Hub billing schedule", "/finance/billing");
        break;
      }
      case "objectives": {
        if (model.synced.connections.hub.mode === "off") break;
        for (const so of model.synced.objectives)
          for (const t of so.tactical) {
            const st = paceOf(t.progress, t.periodStart, t.periodEnd, reg.today).status;
            const kr = t.keyResults[0];
            const hub = t.health.replace(/_/g, " ").toLowerCase();
            const differs = hub && hub !== MEASURE_STATUS_LABEL[st].toLowerCase() && !(st === "behind" && hub === "behind target");
            row(key, t.title, `${kr ? `${kr.title}: ${krValue(kr.current, kr.unit)} of ${krValue(kr.target, kr.unit)} · ` : ""}${MEASURE_STATUS_LABEL[st]}${differs ? ` · Sales Hub says ${hub}` : ""}`, "Sales Hub", null);
          }
        break;
      }
      case "measures": {
        const title = ctx.sectionTitle.trim().toLowerCase();
        for (const d of reg.measures.filter((x) => x.active && x.section.trim().toLowerCase() === title && isLive(x, month))) {
          const at = measureAt(d, month, reg.measureValues, reg.measureTargets, reg.measureSettings);
          const v = at.value;
          row(
            key,
            d.name,
            v ? `${formatMeasure(v.value, d.unit)}${at.target !== null ? ` against ${formatMeasure(at.target, d.unit)}` : ""}${at.status ? ` · ${MEASURE_STATUS_LABEL[at.status]}` : ""}${v.confirmedAt ? "" : " · unconfirmed"}` : "No figure yet",
            "Measures",
            `/measures?open=${d.id}&month=${month}`,
          );
        }
        break;
      }
      case "changes_open":
      case "longest_to_live":
      case "live_unsigned": {
        const s = releaseStats(reg.releases, reg.releaseFrameworks, reg.today);
        if (key === "changes_open") row(key, "Statutory changes still open", `${s.open}${s.overTarget ? `, ${s.overTarget} over target` : ""}`, "Release log", "/releases");
        if (key === "longest_to_live") row(key, "Longest days to go live", s.longest !== null ? plural(s.longest, "day") : "None live yet", "Release log", "/releases");
        if (key === "live_unsigned") row(key, "Live without sign-off", String(s.unsigned), "Release log", "/releases");
        break;
      }
      case "high_risks": {
        const top = reg.riskScale.bands.length - 2;
        const rs = reg.risks.filter((r) => r.status === "open" && bandOf(score(r.residual), reg.riskScale).index >= top);
        const refs = rs.map((r) => riskRef(r, reg.riskDomains.find((d) => d.id === r.domainId)));
        row(key, `Risks rated ${reg.riskScale.bands.slice(-2).map((b) => b.name).join(" or ")}`, rs.length ? `${rs.length}: ${refs.join(", ")}` : "None", "Risks", "/risks");
        break;
      }
      case "obligations": {
        const states = reg.obligations.filter((o) => o.active).map((o) => obligationNow(o, { company: reg.company, yearStartMonth: reg.yearStartMonth }, reg.today).state);
        row(key, "Obligations", `${states.filter((s) => s === "overdue").length} overdue · ${states.filter((s) => s === "due_soon").length} due soon`, "Obligations", "/obligations");
        break;
      }
      case "actions": {
        const open = reg.actions.filter((a) => a.status === "open");
        const late = open.filter((a) => actionState(a, dueOf(a, reg.milestones), reg.today, reg.settings.dueSoonDays) === "overdue").length;
        row(key, "Actions", `${open.length} open, ${late} overdue`, "Decisions & actions", "/decisions/actions");
        break;
      }
      case "decisions_waiting": {
        const ds = reg.decisions.filter((d) => d.status === "proposed" || d.status === "awaiting_consent");
        row(key, "Decisions waiting", ds.length ? `${ds.length}: ${ds.map((d) => decisionRef(d.ref)).join(", ")}` : "None", "Decisions & actions", "/decisions");
        break;
      }
      case "decisions_taken": {
        const period = ctx.months?.length ? ctx.months : [month];
        const ds = reg.decisions.filter((d) => d.status === "taken" && period.includes((d.takenOn ?? d.closedAt?.slice(0, 10) ?? "").slice(0, 7)));
        row(key, `Decisions taken, ${period.length > 1 ? `${packCovers(period[0]!)} to ${packCovers(period.at(-1)!)}` : packCovers(month)}`, ds.length ? ds.map((d) => `${decisionRef(d.ref)} ${d.title}`).join(" · ") : "None", "Decisions & actions", "/decisions");
        break;
      }
      case "actions_list": {
        for (const a of reg.actions.filter((x) => x.status === "open")) {
          const due = dueOf(a, reg.milestones);
          const late = actionState(a, due, reg.today, reg.settings.dueSoonDays) === "overdue";
          row(key, actionRef(a.ref), `${a.title} · ${person(a.ownerId) ?? "no owner"}${due ? ` · due ${dayLabel(due)}` : ""}${late ? " · overdue" : ""}`, "Decisions & actions", `/decisions/actions?open=${a.id}`);
        }
        break;
      }
      case "tier1_risks": {
        const tier = reg.riskTiers.find((t) => t.active);
        for (const r of reg.risks.filter((x) => x.status === "open" && x.tierId === tier?.id)) {
          const ref = riskRef(r, reg.riskDomains.find((d) => d.id === r.domainId));
          const s2 = score(r.residual);
          row(key, ref, `${r.statement} · ${s2} ${bandOf(s2, reg.riskScale).band.name} · ${person(r.ownerId) ?? "no owner"}`, "Risks", `/risks?open=${r.id}`);
        }
        break;
      }
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* The pack as a document                                              */
/* ------------------------------------------------------------------ */

export interface GlanceRow {
  name: string;
  value: string;
  target: string | null;
  status: MeasureStatus | null;
  unconfirmed: boolean;
  notLive: boolean;
}

export interface DocSection {
  id: string;
  title: string;
  ask?: BoardAsk | null;
  /** Compiled from the monthly packs: nothing written here. */
  compiled?: boolean;
  owners: string[];
  state: SectionState;
  approvedBy: string | null;
  panel: PanelRow[];
  fields: { label: string; text: string }[];
}

/** What a pack says: frozen as it is on issue, or live for a draft. */
export interface PackDocument {
  /** Missing on packs issued before board packs: monthly. */
  kind?: PackKind;
  month: string;
  /** "September 2026", or "October to December 2026". */
  covers: string;
  /** A board pack's chair and minute-taker. */
  chair?: string | null;
  minutesBy?: string | null;
  /** A board pack's monthly packs. */
  assembledFrom?: string[];
  meetingOn: ISODate;
  dueOn: ISODate;
  /** Null for a draft. */
  version: number | null;
  issuedAt: string | null;
  issuedBy: string | null;
  circulatedTo: string[];
  /** Sections not approved when it was issued. */
  gaps: string[];
  glance: GlanceRow[];
  sections: DocSection[];
  obligations: { ref: string; title: string; owner: string | null; due: ISODate | null; state: string }[];
  /** The charts at the back, as they stood. Missing from packs issued before there were any. */
  appendices?: Appendix[];
  footer: string;
}

export interface PackIssueSummary {
  id: string;
  packId: string;
  kind: PackKind;
  covers: string;
  month: string;
  version: number;
  issuedAt: string;
  issuedBy: string | null;
  gaps: number;
}

export function packDocument(
  pack: Pick<Pack, "kind" | "month" | "months" | "meetingOn" | "dueOn" | "entries">,
  sections: PackSection[],
  settings: Pick<PackSettings, "footer" | "appendices" | "chartMonths" | "chairId" | "minutesById">,
  ctx: Omit<PackContext, "month">,
  issue: { version: number; issuedAt: string; issuedBy: string | null } | null,
): PackDocument {
  const { reg } = ctx;
  const name = (id: string | null) => (id ? (reg.people.find((p) => p.id === id)?.name ?? null) : null);
  const live = sectionsOf(pack, sections);
  const board = pack.kind === "board";
  // a compiled field: each month's monthly pack, that section's field
  const compiled = (from: NonNullable<PackField["from"]>) =>
    pack.months
      .map((m) => {
        const mp = reg.packs.find((p) => p.kind === "monthly" && p.month === m);
        const text = mp ? (entryFor(mp, from.sectionId).fields[from.fieldId] ?? "").trim() : "";
        return text ? `${packCovers(m)}: ${text}` : "";
      })
      .filter(Boolean)
      .join("\n\n");
  const docSections: DocSection[] = live.map((s) => {
    const e = entryFor(pack, s.id);
    const whole = isCompiled(s);
    return {
      id: s.id,
      title: s.title,
      ask: s.ask,
      compiled: whole,
      owners: whole ? [] : s.ownerIds.map(name).filter((x): x is string => !!x),
      state: whole ? "approved" : e.state,
      approvedBy: !whole && e.state === "approved" ? name(e.approvedBy) : null,
      panel: panelRows(s.panels, { ...ctx, month: pack.month, months: pack.months, sectionTitle: s.title }),
      fields: s.fields.map((f) => ({ label: f.label, text: f.from ? compiled(f.from) : (e.fields[f.id] ?? "") })),
    };
  });
  const glance: GlanceRow[] = reg.measures
    .filter((d) => d.active && d.headline)
    .map((d) => {
      const at = measureAt(d, pack.month, reg.measureValues, reg.measureTargets, reg.measureSettings);
      return { name: d.name, value: formatMeasure(at.value?.value, d.unit), target: at.target !== null ? formatMeasure(at.target, d.unit) : null, status: at.status, unconfirmed: !!at.value && !at.value.confirmedAt, notLive: !isLive(d, pack.month) };
    });
  const obligations = reg.obligations
    .filter((o) => o.active)
    .map((o) => ({ o, now: obligationNow(o, { company: reg.company, yearStartMonth: reg.yearStartMonth }, reg.today) }))
    .filter(({ now }) => now.state === "overdue" || now.state === "due_soon")
    .sort((a, b) => (a.now.due ?? "").localeCompare(b.now.due ?? ""))
    .map(({ o, now }) => ({ ref: obligationRef(o.ref), title: o.title, owner: name(o.ownerId), due: now.due, state: OBLIGATION_STATE_LABEL[now.state] }));
  return {
    kind: pack.kind,
    month: pack.month,
    covers: packPeriod(pack),
    ...(board ? { chair: name(settings.chairId), minutesBy: name(settings.minutesById), assembledFrom: pack.months.map(packCovers) } : {}),
    meetingOn: pack.meetingOn,
    dueOn: pack.dueOn,
    version: issue?.version ?? null,
    issuedAt: issue?.issuedAt ?? null,
    issuedBy: issue?.issuedBy ?? null,
    circulatedTo: reg.people.filter((p) => p.active && p.role !== "observer").map((p) => p.name),
    gaps: docSections.filter((s) => s.state !== "approved").map((s) => s.title),
    glance,
    sections: docSections,
    obligations,
    appendices: packAppendices(settings.appendices, { ...ctx, month: pack.month }, settings.chartMonths),
    footer: settings.footer,
  };
}
